import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

import Loader from "../../../components/loader";
import Comments from "../../../components/Comments";
import api from "../../../services/api";
import SelectUser from "../../../components/selectUser";

export default ({ cashout, setCashout }) => {
  if (!cashout) return <Loader />;

  const user = useSelector((state) => state.Auth.user);
  const navigate = useNavigate();

  const handleAddLead = async (selected) => {
    try {
      const { data } = await api.put(`/cashout/${cashout._id}`, { leadId: selected?._id, leadName: selected?.name });
      setCashout(data);
      toast.success("Lead updated!");
    } catch (err) {
      toast.error("Failed to update lead");
    }
  };

  const handleRemoveLead = async () => {
    try {
      const { data } = await api.put(`/cashout/${cashout._id}`, { leadId: null, leadName: null });
      setCashout(data);
      toast.success("Lead removed!");
    } catch (err) {
      toast.error("Failed to remove lead");
    }
  };

  async function onDelete() {
    if (!window.confirm("Are you sure you want to delete this cashout?")) return;
    await api.remove(`/cashout/${cashout._id}`);
    toast.success("Cashout deleted!");
    navigate("/cashout");
  }

  return (
    <div className="flex flex-col gap-4 p-2">
      <div className="flex-1 flex flex-col gap-4">
        <div className="max-w-lg flex w-full items-center gap-x-4">
          <div className="my-4">
            <label className="block text-sm font-medium text-gray-700">Next action Date</label>
            <input
              type="date"
              value={cashout.date ? cashout.date.split("T")[0] : ""}
              onChange={async (e) => {
                const { data } = await api.put(`/cashout/${cashout._id}`, { date: e.target.value });
                setCashout(data);
                toast.success("Collection date updated!");
              }}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
          <div className="my-4">
            <label className="block text-sm font-medium text-gray-700">Collection Status</label>
            <select
              value={cashout.status || "Late payment"}
              onChange={async (e) => {
                const { data } = await api.put(`/cashout/${cashout._id}`, { status: e.target.value });
                setCashout(data);
                toast.success("Collection status updated!");
              }}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
              <option value="Timetable sent">Timetable sent</option>
              <option value="Signed timetable">Signed timetable</option>
              <option value="Payment in progress">Payment in progress</option>
              <option value="Late payment">Late payment</option>
              <option value="Late payment in progress">Late payment in progress</option>
              <option value="In recovery">In recovery</option>
            </select>
          </div>
          <div className="my-4">
            <label className="block text-sm font-medium text-gray-700">Lead</label>
            {user?.role === "admin" ? (
              <SelectUser
                value={cashout.leadId ? { _id: cashout.leadId, name: cashout.leadName } : null}
                userRoles={["admin"]}
                placeholder="Select lead"
                onChange={(selected) => {
                  if (!selected) {
                    handleRemoveLead();
                    return;
                  }
                  handleAddLead(selected);
                }}
                buttonClassName="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            ) : (
              <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2">
                {cashout.leadName || <span className="text-gray-400">No lead assigned</span>}
              </div>
            )}
          </div>
        </div>

        <Comments
          value={cashout.comments}
          onAdd={async (text) => {
            const comments = [
              ...cashout.comments,
              {
                text,
                user_id: user._id,
                user_name: user.name,
                user_avatar: user.avatar,
              },
            ];
            const { data } = await api.put(`/cashout/${cashout._id}`, { comments });
            setCashout(data);
            toast.success("Comment added!");
          }}
          onDelete={async (comment_id) => {
            const comments = cashout.comments.filter((c) => c._id !== comment_id);
            const { data } = await api.put(`/cashout/${cashout._id}`, { comments });
            setCashout(data);
            toast.success("Comment deleted!");
          }}
        />
      </div>

      <div className="space-y-4">
        <Invoices cashout={cashout} />
        <Payments cashout={cashout} />
      </div>

      <div>
        <button className="bg-[#0560FD] text-[#fff] py-[12px] px-[22px] h-[48px] rounded-[10px] text-sm font-medium hover:bg-[#0451d4] transition-colors" onClick={onDelete}>
          Delete
        </button>
      </div>
    </div>
  );
};

const Invoices = ({ cashout }) => {
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    get();
  }, []);

  async function get() {
    const { data } = await api.post(`/invoice/search`, { projectId: cashout.projectId });
    setInvoices(data);
  }

  function calculateAction(invoice) {
    if (invoice.received === "yes") return "-";
    if (invoice.sent !== "yes") return "Send Invoice";

    const currentDate = new Date();
    const dueDate = new Date(invoice.dueAt);
    const daysPastDue = (currentDate - dueDate) / (1000 * 60 * 60 * 24);

    if (daysPastDue > 45) {
      const date = new Date(dueDate.getTime() + 45 * 24 * 60 * 60 * 1000).toLocaleDateString("en-CA");
      return `If no news at D+45, Mathieu starts (${date})`;
    }
    if (daysPastDue > 30) {
      const date = new Date(dueDate.getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString("en-CA");
      return `D+30, message from Seb (${date})`;
    }
    if (daysPastDue > 27) {
      const date = new Date(dueDate.getTime() + 27 * 24 * 60 * 60 * 1000).toLocaleDateString("en-CA");
      return `D+27, message + échéancier to be negotiated/signed (${date})`;
    }
    if (daysPastDue > 20) {
      const date = new Date(dueDate.getTime() + 20 * 24 * 60 * 60 * 1000).toLocaleDateString("en-CA");
      return `D+20, last reminder + message late penalties to apply and negotiate (${date})`;
    }
    if (daysPastDue > 12) {
      const date = new Date(dueDate.getTime() + 12 * 24 * 60 * 60 * 1000).toLocaleDateString("en-CA");
      return `D+12, second reminder (${date})`;
    }
    if (daysPastDue > 2) {
      const date = new Date(dueDate.getTime() + 2 * 24 * 60 * 60 * 1000).toLocaleDateString("en-CA");
      return `D+2, first reminder (${date})`;
    }

    return "Waiting for Payment";
  }

  if (invoices.length === 0) return <div className="text-gray-500">No invoices found</div>;

  const totalRemaining = invoices
    .filter((e) => e.received === "no" && e.sent === "yes")
    .reduce((a, b) => a + b.totalRemaining, 0)
    .toFixed(2);

  return (
    <div className="border border-gray-200 p-4 rounded-lg text-sm">
      <div className="flex items-center gap-4">
        <h1 className="text-lg font-medium">Invoices</h1>
        <div className="text-red-950 border border-red-950 rounded-md py-1 px-3">Total Remaining: {totalRemaining} €</div>
      </div>

      <div className="overflow-x-auto mt-4">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent date</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due date</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {invoices.map((e) => (
              <tr key={e._id} className="hover:bg-gray-50 transition-colors">
                <td className="px-3 py-2 max-w-56">
                  <a className="text-blue-600 hover:text-blue-800 underline" href={`/invoice/${e._id}`} target="_blank">
                    {e.index && `#${e.index}- `}
                    {e.name}
                  </a>
                </td>
                <td className="px-3 py-2">{(e.sentAt || "").split("T")[0]}</td>
                <td className="px-3 py-2">{(e.dueAt || "").split("T")[0]}</td>
                <td className="px-3 py-2">{e.total.toFixed(2)}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    {e.sent === "yes" ? (
                      <span className="border border-green-500 rounded px-2 py-0.5 text-xs text-green-500">Sent</span>
                    ) : (
                      <span className="border border-gray-500 rounded px-2 py-0.5 text-xs text-gray-500">Not sent</span>
                    )}
                    {e.received === "yes" ? (
                      <span className="border border-green-500 rounded px-2 py-0.5 text-xs text-green-500">Received</span>
                    ) : e.sent === "yes" ? (
                      <span className="border border-red-500 rounded px-2 py-0.5 text-xs text-red-500">Not received</span>
                    ) : null}
                  </div>
                </td>
                <td className="px-3 py-2 max-w-72 font-medium">{calculateAction(e)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Payments = ({ cashout }) => {
  const [banks, setBanks] = useState([]);

  useEffect(() => {
    get();
  }, []);

  async function get() {
    const { data } = await api.post(`/bank/search`, { projectId: cashout.projectId, category: "INCOME" });
    setBanks(data.banks);
  }

  if (banks.length === 0) return <div className="text-gray-500">No payments found</div>;

  return (
    <div className="border border-gray-200 p-4 rounded-lg text-sm">
      <h1 className="text-lg font-medium mb-4">Payments</h1>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {banks.map((e) => (
              <tr key={e._id} className="hover:bg-gray-50 transition-colors">
                <td className="px-3 py-2">{e.name}</td>
                <td className="px-3 py-2">{e.date}</td>
                <td className="px-3 py-2">{e.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
